import get from 'lodash/get';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet-async';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Portal, Button} from '@spothero/ui-backlog';
import GTMUtils from 'utils/gtm';
import withConfig from '@/config/withConfig';
import SHLoader from 'common/loader';
import {
    getAirportTravelDistance,
    updateSelected,
} from 'store/spot/spot-actions';
import {fetchRates} from 'store/checkout/checkout-actions';
import {push, goBack} from 'store/router/router-actions';
import {updateTimes} from 'store/search-request/search-request-actions';
import trackAvailabilityCheck from 'segment/events/availability-check';
import trackSpotDetails from 'segment/pages/spot-details';
import SHUtils from 'utils/sh-utils';
import SpotImages from 'common/spot-details/spot-images';
import ShuttleInformation from './components/shuttle-information';
import BenefitMessage from './components/benefit-message';
import SpotDetailsHeader from './components/header';
import FacilityLocation from './components/facility-location';
import SafetyAmenities from './components/safety-amenities';
import ThingsYouShouldKnow from 'common/spot-details/things-you-should-know';
import HowToRedeemArrivalDeparture from './components/arrival-departure-redemption-instructions';
import FAQPanels from 'common/spot-details/faq';
import TrustSealValueProps from 'common/spot-details/trust-seal-and-value-props';
import AirportPurchaseInformation from './components/purchase-information-desktop-sidebar';
import AirportPurchaseSnapshot from './components/purchase-information-mobile-snapshot';
import AccessHours from 'common/spot-details/access-hours';
import IconArrowLeft from '@spothero/icons/arrow-left';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import DateTimeRangeModal from 'pages/search-airport/components/search-results/DateTimeRangeModal';
import {CarouselVariant} from 'common/image-carousel';
import {GoogleMapsProvider} from 'common/google-map-context';

class SpotDetailsAirport extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        match: PropTypes.shape({
            params: PropTypes.shape({
                spotId: PropTypes.string,
            }),
        }).isRequired,
        user: PropTypes.object,
        city: PropTypes.object,
        destination: PropTypes.object,
        rateSessionId: PropTypes.string,
        currencyType: PropTypes.string,
        spot: PropTypes.object,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        referrer: PropTypes.string,
        referrerAffiliate: PropTypes.string,
        updateSelectedSpot: PropTypes.func,
        updateSearchTimes: PropTypes.func,
        getSpotToAirportTravelDistance: PropTypes.func,
        fetchAirportRates: PropTypes.func,
        isLoading: PropTypes.bool,
        pushTo: PropTypes.func,
        goBackToPreviousPage: PropTypes.func,
        apiDateTimeFormat: PropTypes.string,
    };
    _lastActionType = 'SEARCH';
    _bodyClasses = SHUtils.determineInitialLoadBodyClasses({
        isAirport: true,
        pageType: 'facility',
    });
    state = {
        showConfirmTimeModal: false,
        confirmTimeModalEditMode: false,
        confirmTimeModalErrorMessage: null,
        start: null,
        end: null,
        isValidStart: true,
    };

    componentDidMount() {
        const {
            match: {
                params: {spotId},
            },
            starts,
            ends,
            fetchAirportRates,
        } = this.props;

        fetchAirportRates({
            spotId: parseInt(spotId, 10),
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'spotdetail-airport',
        });

        SHUtils.staticScrollTo();
    }

    componentDidUpdate(prevProps) {
        const {
            rateSessionId,
            user: {id: userId, status: userStatus},
            isMonthly: monthly,
            starts,
            ends,
            referrer,
            referrerAffiliate,
            city,
            destination,
            spot,
            getSpotToAirportTravelDistance,
        } = this.props;

        const {spot: prevSpot, rateSessionId: prevRateSessionId} = prevProps;

        if (!prevSpot && spot) {
            getSpotToAirportTravelDistance(spot).catch(() => {
                // silently catch this error to ignore errors being logged
            });

            trackSpotDetails({
                userId,
                city,
                destination,
                monthly,
                starts,
                ends,
                spot,
                referrer,
                referrerAffiliate,
            });
        }

        if (prevRateSessionId !== rateSessionId) {
            // a new rate load was initiated which signifies an availability check
            trackAvailabilityCheck({
                searchRequest: {
                    monthly,
                    starts,
                    ends,
                },
                city,
                spot,
                userStatus,
                screen: 'spot details',
                actionType: this._lastActionType,
            });
        }
    }

    _onApplyDateTimeChange = ({starts, ends}) => {
        const {
            spot: {
                facility: {id: spotId},
            },
            fetchAirportRates,
        } = this.props;

        this.setState({
            showConfirmTimeModal: false,
            confirmTimeModalEditMode: false,
            confirmTimeModalErrorMessage: null,
        });

        this._lastActionType = 'UPDATE_REQUESTED_TIMES';

        fetchAirportRates({
            spotId,
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });
    };

    _onHideConfirmDateTimeModal = () => {
        this.setState({
            showConfirmTimeModal: false,
            confirmTimeModalEditMode: false,
            confirmTimeModalErrorMessage: null,
        });
    };

    _onShowConfirmDateTimeModal = isEditMode => {
        this.setState({
            showConfirmTimeModal: true,
            confirmTimeModalEditMode: isEditMode || false,
        });
    };

    _onCheckoutClick = () => {
        const {
            spot: {
                selectedRate: {url},
            },
            pushTo,
        } = this.props;

        pushTo(url);
    };

    _onHandleBackClick = () => {
        this.props.goBackToPreviousPage();
    };

    _onDateTimeChange = ({
        start: updatedStart,
        end: updatedEnd,
        isValidStart: isValidUpdatedStart,
    }) => {
        const {apiDateTimeFormat} = this.props;

        this.setState(prevState => ({
            ...prevState,
            isValidStart: isValidUpdatedStart,
        }));
        if (!isValidUpdatedStart) {
            return;
        }
        this.setState(prevState => ({
            ...prevState,
            start: updatedStart.format(apiDateTimeFormat),
            end: updatedEnd.format(apiDateTimeFormat),
        }));
    };

    _onUpdateDateTime = () => {
        const {start, end} = this.state;
        const {starts, ends} = this.props;

        if (!start || !end) {
            this._onApplyDateTimeChange({starts, ends});

            return;
        }

        const updatedStart = start || starts;
        const updatedEnd = end || ends;

        this._onApplyDateTimeChange({
            starts: updatedStart,
            ends: updatedEnd,
        });
    };

    render() {
        const {
            isMobile,
            currencyType,
            spot,
            starts,
            ends,
            updateSearchTimes,
            fetchAirportRates,
            isLoading,
            pushTo,
            updateSelectedSpot,
        } = this.props;
        const {showConfirmTimeModal, isValidStart} = this.state;
        let facility = null;
        let selectedRate = null;
        let linearMeters = null;
        let comparisons = null;

        if (spot) {
            facility = spot.facility;
            selectedRate = spot.selectedRate;
            comparisons = spot.comparisons;
            linearMeters = spot.distance;
        }

        if (isLoading || !facility) {
            return <SHLoader className="SpotDetailsAirport-loader" />;
        }

        const {
            airport,
            cancellation_allowed: cancellationAllowed,
            amenities_full: amenities,
            rating_info: ratingInfo,
            images,
            title,
            available_transient_inventory: availableTransientInventory,
            restrictions: facilityRestrictions,
        } = facility;
        const {
            details: {code: iataCode},
            shuttle,
            lowest_daily_rate: lowestDailyRate,
        } = airport;
        const displayPrice = selectedRate
            ? selectedRate.display_price
            : lowestDailyRate;
        const hasInOutAmenity = Boolean(
            amenities.find(amenity => amenity.slug === 'in-out')
        );

        const {star_rating: starRating} = ratingInfo;

        const airportPercentLowerPrice = get(
            comparisons,
            'airport_percent_lower_price',
            0
        );

        const showAirportPercentLowerMessage =
            airportPercentLowerPrice >= 40 && starRating > 4;

        const showBenefitMessage =
            !isEmpty(comparisons) && showAirportPercentLowerMessage;

        const backButtonClasses = classNames('BackButton', {
            showBelowBanner: showBenefitMessage,
            noBannerBackButton: !showBenefitMessage,
        });

        return (
            <GoogleMapsProvider>
                <div className="SpotDetailsAirport">
                    <Helmet>
                        <meta name="robots" content="noindex, follow" />
                        <body className={this._bodyClasses} />
                    </Helmet>
                    <div className="SpotDetailsAirport-main-content">
                        {isMobile && (
                            <Button
                                className={backButtonClasses}
                                onClick={this._onHandleBackClick}
                            >
                                {/* span allows CSS to create larger tap target for this button */}
                                <span>
                                    <IconArrowLeft />
                                </span>
                            </Button>
                        )}
                        {showBenefitMessage && (
                            <BenefitMessage
                                percentLowerPrice={airportPercentLowerPrice}
                                iataCode={iataCode}
                            />
                        )}
                        <SpotImages
                            images={images}
                            alt={title}
                            carouselVariant={CarouselVariant.SINGLE}
                        />
                        <SpotDetailsHeader
                            facility={facility}
                            percentLowerPrice={airportPercentLowerPrice}
                            linearMeters={linearMeters}
                            walkingMeters={null}
                            showBenefitMessage={showBenefitMessage}
                        />
                        <ShuttleInformation
                            shuttle={shuttle}
                            facilityTitle={title}
                            iataCode={iataCode}
                        />
                        <AccessHours
                            facility={facility}
                            rate={selectedRate}
                            sectionTitle="Facility Access Hours"
                        />
                        <FacilityLocation
                            facility={facility}
                            linearMeters={linearMeters}
                            walkingMeters={null}
                        />
                        <SafetyAmenities amenities={amenities} />
                        <ThingsYouShouldKnow
                            hasInOutAmenity={hasInOutAmenity}
                            restrictions={facilityRestrictions}
                        />
                        <HowToRedeemArrivalDeparture facility={facility} />
                        <FAQPanels cancellationAllowed={cancellationAllowed} />
                        <TrustSealValueProps />
                    </div>
                    {isMobile ? (
                        <Portal>
                            <AirportPurchaseSnapshot
                                cancellationAllowed={cancellationAllowed}
                                availableTransientInventory={
                                    availableTransientInventory
                                }
                                displayPrice={displayPrice}
                                currencyType={currencyType}
                                checkoutDisabled={!selectedRate}
                                onCheckoutClick={this._onCheckoutClick}
                            />
                        </Portal>
                    ) : (
                        <div className="SpotDetailsAirport-checkout">
                            <AirportPurchaseInformation
                                onCheckoutClick={this._onCheckoutClick}
                                facility={facility}
                                selectedRate={selectedRate}
                                starts={starts}
                                ends={ends}
                                availableTransientInventory={
                                    availableTransientInventory
                                }
                                fetchRates={fetchAirportRates}
                                updateSearchTimes={updateSearchTimes}
                                isFetchingRates={isLoading}
                                pushTo={pushTo}
                                updateSelectedSpot={updateSelectedSpot}
                                displayPrice={displayPrice}
                                currencyType={currencyType}
                                onShowConfirmDateTimeModal={
                                    this._onShowConfirmDateTimeModal
                                }
                            />
                        </div>
                    )}
                    {showConfirmTimeModal && (
                        <DateTimeRangeModal
                            submitDisabled={!isValidStart}
                            onUpdateDateTime={this._onUpdateDateTime}
                            onDateTimeChange={this._onDateTimeChange}
                            onModalClose={this._onHideConfirmDateTimeModal}
                            modalTitle="Change Reservation Date and Time"
                        />
                    )}
                </div>
            </GoogleMapsProvider>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {data: destination},
        checkout: {rateSessionId, currencyType, isFetchingRates, checkoutError},
        search: {
            data: {referrerAffiliate},
        },
        searchRequest: {monthly, airport, starts, ends, referrer},
        spot: {selected},
        user: {data: user},
    } = state;

    return {
        isLoading: isFetchingRates,
        rateSessionId,
        checkoutError,
        user,
        city,
        destination,
        isMonthly: monthly,
        isAirport: airport,
        starts,
        ends,
        referrer,
        referrerAffiliate,
        spot: selected,
        currencyType,
    };
};

const mapDispatchToProps = {
    updateSelectedSpot: updateSelected,
    fetchAirportRates: fetchRates,
    getSpotToAirportTravelDistance: getAirportTravelDistance,
    updateSearchTimes: updateTimes,
    pushTo: push,
    goBackToPreviousPage: goBack,
};

const enhance = compose(
    withConfig(['isMobile', 'apiDateTimeFormat']),
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotDetailsAirport);
